/**
 * V2Logo — tone-aware, variant-aware TravelTrucks logo lockup.
 *
 * Renders the real TravelTrucks mountain-and-wave lockup from `/brand/`.
 *
 *   tone="icy"  → for DARK backgrounds (reverse: near-white wordmark, orange kept)
 *   tone="navy" → for LIGHT backgrounds (charcoal wordmark, orange kept)
 *
 *   variant="main" / "main+tagline" / "compact+tagline" → the full lockup (~2:1)
 *   variant="symbol"                                     → the mountain mark only
 *
 * `swapOnScroll` crossfades the two tones as the logo passes over a dark hero
 * into a light scrolled state (kept for any remaining scroll-aware header).
 */
import React from 'react';

const TT_LOGO         = '/brand/traveltrucks-logo.svg';          // charcoal + orange (light bg)
const TT_LOGO_REVERSE = '/brand/traveltrucks-logo-reverse.svg';  // near-white + orange (dark bg)
const TT_MARK         = '/brand/traveltrucks-icon.svg';          // mountain mark only
const LOCKUP_RATIO    = 1250 / 620.82; // ≈ 2.01 — the TravelTrucks lockup

const SRC = {
  'main':            { icy: TT_LOGO_REVERSE, navy: TT_LOGO, ratio: LOCKUP_RATIO },
  'main+tagline':    { icy: TT_LOGO_REVERSE, navy: TT_LOGO, ratio: LOCKUP_RATIO },
  'compact+tagline': { icy: TT_LOGO_REVERSE, navy: TT_LOGO, ratio: LOCKUP_RATIO },
  'symbol':          { icy: TT_MARK,         navy: TT_MARK, ratio: 1 },
};

export default function V2Logo({
  variant = 'main',
  tone = 'navy',
  height = 32,
  swapOnScroll = false,
  scrolled = false,           // when swapOnScroll=true: false→icy (over dark), true→navy (scrolled to light)
  className = '',
  alt = 'TravelTrucks',
  'data-testid': testId,
}) {
  const entry = SRC[variant] || SRC.main;
  const style = { height, width: 'auto', display: 'block' };

  if (swapOnScroll) {
    // Render both, crossfade via opacity — keeps SSR safe and avoids flashes.
    return (
      <span
        className={`relative inline-block ${className}`}
        style={{ height, lineHeight: 0 }}
        data-testid={testId}
      >
        <img
          src={entry.icy}
          alt={alt}
          style={{ ...style, opacity: scrolled ? 0 : 1, transition: 'opacity .2s' }}
          draggable={false}
          aria-hidden={scrolled}
        />
        <img
          src={entry.navy}
          alt=""
          style={{
            ...style,
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: scrolled ? 1 : 0,
            transition: 'opacity .2s',
          }}
          draggable={false}
          aria-hidden={!scrolled}
        />
      </span>
    );
  }

  const src = tone === 'icy' ? entry.icy : entry.navy;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      draggable={false}
      data-testid={testId}
    />
  );
}
